import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  DialogComponent,
  ModalComponent,
  UploadFileForm,
} from "../../../components";
import { useAppDesignPdf, useRequest } from "../../../hooks";
import {
  ClientProps,
  DocumentProps,
  WellProps,
} from "../../../interfaces/interfaces";
import { DocRow, DocumentsPortalLayout } from "../../../components/documents/DocumentsPortalLayout";
import DataContext from "../../../context/DataContext";
import { useNavigate } from "react-router-dom";

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const latestUploadByProposalId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    const pid = row.proposalId;
    if (!pid) return;
    const cur = map.get(pid);
    if (!cur || new Date(row.date).getTime() > new Date(cur.date).getTime())
      map.set(pid, row);
  });
  return map;
};

export const ProposalAdmin = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { downloadProposalPdf } = useAppDesignPdf();
  const { clientOptions, fetchDataClient, onDeleteDocument } = useContext(DataContext);

  const [wells, setWells] = useState<WellProps[]>([]);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);

  useEffect(() => { fetchDataClient(); }, []);

  useEffect(() => {
    setTimeout(() => { setIsError(false); setIsSuccess(false); }, 4000);
  }, [isError, isSuccess]);

  const handleClientChange = (client: ClientProps | null) => {
    setSelectedClient(client);
    setSelectedWellId(null);
    if (!client) { setWells([]); setRows([]); return; }
    const opts: RequestInit = { method: "GET" };
    setWellsLoading(true);
    setRowsLoading(true);
    Promise.all([
      handleRequest({ endpoint: `well/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `proposal-list/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `proposal-detail?client=${client.id}`, options: opts }),
    ])
      .then(([wellsRes, uploadedRes, appRes]) => {
        setWells(isSuccessful(wellsRes) ? wellsRes.data : []);
        setWellsLoading(false);
        const appRows = getRows(appRes);
        const appById = new Map<number, any>(appRows.map((i: any) => [i.id, i]));
        const uploaded: DocRow[] = (isSuccessful(uploadedRes) ? uploadedRes.data : []).map(
          (item: any) => {
            const linked = appById.get(item.proposalId);
            return {
              rowId: `u-${item.id}`,
              customName: item.customName,
              date: item.date ?? "",
              wellId: item.idWell,
              wellName: "",
              source: "uploaded" as const,
              path: item.path,
              owner: linked?.createdBy ?? "—",
              proposalId: item.proposalId,
            };
          },
        );
        const uploadsByProposal = latestUploadByProposalId(uploaded);
        const app: DocRow[] = appRows
          .map((item: any) => {
            const def = uploadsByProposal.get(item.id);
            return {
              rowId: `a-${item.id}`,
              customName: item.customName,
              date: item.date ?? "",
              wellId: item.well?.id,
              wellName: item.well?.name ?? "",
              source: "app" as const,
              appId: item.id,
              owner: item.createdBy ?? "—",
              defaultUploadPath: def?.path,
              defaultUploadName: def?.customName,
            };
          })
          .filter((r: DocRow) => r.appId == null || !uploadsByProposal.has(r.appId ?? -1));
        const wellMap = new Map<number, string>(
          (isSuccessful(wellsRes) ? wellsRes.data : []).map((w: WellProps) => [w.id, w.name]),
        );
        const withNames = uploaded.map((r) => ({
          ...r,
          wellName: wellMap.get(r.wellId ?? -1) ?? "",
        }));
        const sorted = [...withNames, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
        setRowsLoading(false);
      })
      .catch(() => { setIsError(true); setWellsLoading(false); setRowsLoading(false); });
  };

  const fetchProposalByKey = (key: string, name: string, download: boolean) => {
    setIsCreatingReport(true);
    const opts: RequestInit = {
      method: "POST",
      body: JSON.stringify({ key }),
      headers: { responseType: "Buffer" } as any,
    };
    handleRequest({ endpoint: "pdf-proposal-adm", options: opts })
      .then((res) => {
        if (res.success) {
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          if (download) {
            const link = document.createElement("a");
            link.href = url; link.setAttribute("download", name);
            document.body.appendChild(link); link.click();
          } else {
            window.open(url);
          }
        } else setIsError(true);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsCreatingReport(false));
  };

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      fetchProposalByKey(row.path, row.customName, true);
    } else if (row.source === "app") {
      if (row.defaultUploadPath) {
        fetchProposalByKey(row.defaultUploadPath, row.defaultUploadName ?? row.customName, true);
      } else if (row.appId != null) {
        await downloadProposalPdf(row.appId, row.customName);
      }
    }
  };

  const handleUploadProposal = async ({
    client, well, customName, file, installationDate,
  }: DocumentProps) => {
    try {
      setIsCreatingReport(true);
      setIsModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      customName && formData.append("customName", customName);
      installationDate && formData.append("installationDate", installationDate.toISOString());
      formData.append("type", "proposal");
      formData.append("file", file, file.name);
      const res = await (await fetch(process.env.REACT_APP_DEV_API + "/proposal-upload", { method: "POST", body: formData })).json();
      if (res.success) { setIsSuccess(true); selectedClient && handleClientChange(selectedClient); }
      else setIsError(true);
    } catch { setIsError(true); }
    finally { setIsCreatingReport(false); }
  };

  return (
    <>
      <DocumentsPortalLayout
        title="Proposals"
        subtitle="Browse, filter and download proposals"
        wells={wells}
        wellsLoading={wellsLoading}
        rows={rows}
        rowsLoading={rowsLoading}
        selectedWellId={selectedWellId}
        onWellSelect={setSelectedWellId}
        onDownload={handleDownload}
        noWellMessage="Select a client to load their proposals"
        isAdmin
        clientOptions={clientOptions}
        onClientChange={handleClientChange}
        onUpload={() => setIsModalOpen(true)}
        onOpenInNew={(row) => row.path && fetchProposalByKey(row.path, row.customName, false)}
        onDelete={(row) => { setIdToDelete(row.rowId.replace("u-", "")); setIsOpenAlert(true); }}
        onEdit={(row) => navigate(`/tech/${row.appId}`)}
        isError={isError}
      />

      {isCreatingReport && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1300 }} />
      )}
      {isSuccess && <AlertComponent type="success" />}
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={clientOptions}
              wellOptions={[]}
              type="proposal"
              fetchDataWells={(id) => handleRequest({ endpoint: `well/client/${id}`, options: { method: "GET" } })}
              onUploadFile={(v: DocumentProps) => handleUploadProposal(v)}
              onCancel={() => setIsModalOpen(false)}
            />
          }
        />
      )}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this Proposal from the Cloud?</div>}
          onAccept={() => {
            onDeleteDocument("proposal-file", idToDelete, () => selectedClient && handleClientChange(selectedClient));
            setIdToDelete(""); setIsOpenAlert(false);
          }}
          onCancel={() => { setIsOpenAlert(false); setIdToDelete(""); }}
        />
      )}
    </>
  );
};
